import { useEffect, useRef, useState } from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';

import { AuthLayout } from '@/components/layouts/AuthLayout';
import { ClientAuthBrandPanel } from '@/components/layouts/components/ClientAuthBrandPanel';
import { Alert } from '@/components/ui/alert';
import {
  AuthFootnote,
  authFootnoteActionClass,
} from '@/features/auth/components/auth-ui/AuthFootnote';
import { AuthHeading } from '@/features/auth/components/auth-ui/AuthHeading';
import { AuthPendingCard } from '@/features/auth/components/auth-ui/AuthPendingCard';
import { AUTH_COLOR, authSpacing } from '@/features/auth/components/auth-ui/authTokens';
import { authApi } from '@/features/auth/services/authApi';
import {
  exchangeSsoTicket,
  normalizeSsoUser,
  readSsoLandingParams,
} from '@/features/auth/services/ssoApi';
import { cn } from '@/lib/utils';
import { useAuthStore } from '@/stores/authStore';

export const Route = createFileRoute('/auth/sso/land')({
  component: SsoLandPage,
});

function SsoLandPage() {
  const navigate = useNavigate();

  // Resolve the landing params once, synchronously, so the initial error state
  // is derived from the URL at render time rather than set inside the effect.
  const [{ ticket, error }] = useState(readSsoLandingParams);
  const [errorMessage, setErrorMessage] = useState<string | null>(() => {
    if (error) return 'We couldn’t complete your sign-in. Please try again.';
    if (!ticket) return 'This sign-in link is missing or has already been used.';
    return null;
  });

  // The ticket is single-use — guard against StrictMode double-mount and any
  // accidental re-render firing the exchange twice.
  const exchangeStartedRef = useRef(false);

  useEffect(() => {
    if (exchangeStartedRef.current) return;
    exchangeStartedRef.current = true;

    if (!ticket) return;

    void (async () => {
      try {
        const { tokens, user } = await exchangeSsoTicket(ticket);
        // Establish the session with the exchange payload first (defaults fill
        // the fields the partner doesn't carry)…
        useAuthStore.getState().setAuth({
          user: normalizeSsoUser(user),
          accessToken: tokens.accessToken,
          refreshToken: tokens.refreshToken,
          idToken: tokens.idToken,
        });
        // …then hydrate the canonical User the way the rest of the app does and
        // branch on the authoritative `onboarded` flag. A brand-new SSO student
        // is provisioned with firstName/lastName but no dateOfBirth, so they are
        // not onboarded and must land on the form, not the dashboard.
        let onboarded = false;
        try {
          const me = await authApi.getMe();
          if (me.data) useAuthStore.getState().setUser(me.data);
          onboarded = Boolean(me.data?.onboarded);
        } catch {
          // /auth/me failed — the normalized exchange user is good enough to
          // enter the app; the store will re-hydrate on the next request. Default
          // a just-provisioned user to onboarding (safer than a dashboard flash).
          onboarded = false;
        }
        if (onboarded) {
          void navigate({ to: '/client/dashboard', replace: true });
        } else {
          void navigate({
            to: '/client/onboarding',
            search: { step: '1' } as never,
            replace: true,
          });
        }
      } catch {
        setErrorMessage('We couldn’t complete your sign-in. Please try again.');
      }
    })();
  }, [navigate, ticket]);

  return (
    <AuthLayout brandPanel={<ClientAuthBrandPanel />} formSide="right">
      <div className={cn('flex flex-col', authSpacing.stack)}>
        <AuthHeading
          size="sm"
          title={errorMessage ? 'Sign-in failed' : 'Signing you in'}
          subtitle={
            errorMessage
              ? 'We couldn’t finish signing you in. Use the link below to try again.'
              : 'Just a moment while we finish signing you in…'
          }
        />

        {errorMessage ? (
          <>
            <Alert variant="error">{errorMessage}</Alert>
            <AuthFootnote
              prompt="Back to"
              action={
                <Link
                  to={'/sso/bits-wilp' as never}
                  className={authFootnoteActionClass}
                  style={{ color: AUTH_COLOR.blue }}
                >
                  BITS WILP login
                </Link>
              }
            />
          </>
        ) : (
          <AuthPendingCard>Signing in…</AuthPendingCard>
        )}
      </div>
    </AuthLayout>
  );
}
